// Components/ChatComponents/BubbleInfoSidebar.jsx
// ENHANCED SIDEBAR - Modern, intuitive design - FIXED

import React, { useState, useCallback } from 'react';
import {
  Box, Typography, IconButton, Button, Avatar, Chip,
  Stack, Card, CardContent, Divider, Tooltip, CircularProgress,
  Alert, Drawer, Badge
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  FolderOpen as FolderIcon,
  People as PeopleIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  ChevronRight,
  ChevronLeft,
  FiberManualRecord as DotIcon,
  Star as StarIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import API from '../../API';
import { replaceConsoleError } from '../../Utils/toastNotifications';

// Import comprehensive management panels
import BubbleSettingsPanel from '../../Bubbles/BubbleSettingsPanel';
import BubbleOrganizerSidebar from '../../Bubbles/BubbleOrganizerSidebar';
import BubbleParticipantsPanel from '../../Bubbles/BubbleParticipantsPanel';

/* ───────────────────────── Styled Components ───────────────────────── */
const SidebarContainer = styled(Box)(({ theme, minimized }) => ({
  width: minimized ? 60 : 320,
  flexShrink: 0,
  height: '100%',
  borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 0.8)}, ${theme.palette.background.paper})`,
  backdropFilter: 'blur(20px)',
  display: 'flex',
  flexDirection: 'column',
  transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  position: 'relative',
}));

const QuickActionCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  background: alpha(theme.palette.background.paper, 0.4),
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(1.5),
  '&:hover': {
    transform: 'translateX(4px)',
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
    borderColor: alpha(theme.palette.primary.main, 0.3),
    background: alpha(theme.palette.background.paper, 0.9),
    '& .action-icon': {
      color: theme.palette.primary.main,
      transform: 'scale(1.1)',
    },
    '& .chevron-icon': {
      transform: 'translateX(4px)',
    },
  },
  '&:active': {
    transform: 'translateX(2px)',
  },
}));

const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.6)}, ${alpha(theme.palette.background.paper, 0.9)})`,
  backdropFilter: 'blur(10px)',
  boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.03)}`,
}));

const StatusBadge = styled(Box)(({ theme, isOnline }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.5, 1.25),
  borderRadius: theme.spacing(3),
  background: isOnline 
    ? alpha(theme.palette.success.main, 0.1)
    : alpha(theme.palette.warning.main, 0.1),
  border: `1px solid ${isOnline 
    ? alpha(theme.palette.success.main, 0.2)
    : alpha(theme.palette.warning.main, 0.2)}`,
}));

const PulsingDot = styled(DotIcon)(({ theme, isOnline }) => ({
  fontSize: 10,
  color: isOnline ? theme.palette.success.main : theme.palette.warning.main,
  animation: isOnline ? 'pulse 2s ease-in-out infinite' : 'none',
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
  },
}));

const MinimizedIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1.5, 0.5),
  margin: theme.spacing(0.5, 0),
  cursor: 'pointer',
  borderRadius: theme.spacing(1.5),
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    transform: 'scale(1.05)',
    '&::before': {
      opacity: 1,
      transform: 'scaleX(1)',
    },
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%) scaleX(0)',
    width: 3,
    height: '60%',
    borderRadius: '0 4px 4px 0',
    backgroundColor: theme.palette.primary.main,
    opacity: 0,
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  },
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '0.7rem',
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  color: alpha(theme.palette.text.secondary, 0.6),
  padding: theme.spacing(0, 1.5),
  marginBottom: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '&::before': {
    content: '""',
    width: 3,
    height: 3,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
}));

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  background: alpha(theme.palette.background.paper, 0.4),
  backdropFilter: 'blur(20px)',
}));

/* ───────────────────────── Main Component ───────────────────────── */
export default function BubbleInfoSidebar({
  bubbleId,
  conversationId,
  isPolling,
  onNavigateBack,
  minimized,
  onToggleMinimized,
}) {
  // Panel state
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
  const [organizerDrawerOpen, setOrganizerDrawerOpen] = useState(false);
  const [participantsPanelOpen, setParticipantsPanelOpen] = useState(false);

  /* ───────────────────────── Data Fetching ───────────────────────── */
  
  // Fetch bubble metadata
  const {
    data: bubbleData,
    isLoading: bubbleLoading,
    error: bubbleError,
    refetch: refetchBubble
  } = useQuery(
    ['bubble', bubbleId],
    () => API.bubbles.get(bubbleId),
    {
      enabled: !!bubbleId,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30,
      onError: (err) => {
        replaceConsoleError('Error fetching bubble data', err);
      }
    }
  );

  // Fetch bubble members
  const {
    data: membersData,
  } = useQuery(
    ['bubble-members', bubbleId],
    () => API.bubbles.getMembers(bubbleId),
    {
      enabled: !!bubbleId,
      staleTime: 1000 * 60 * 2,
      onError: (err) => {
        replaceConsoleError('Error fetching bubble members', err);
      }
    }
  );

  // Fetch bubble organizers
  const {
    data: organizersData,
    isLoading: organizersLoading,
    refetch: refetchOrganizers,
  } = useQuery(
    ['bubble-organizers', bubbleId],
    () => API.bubbles.getOrganizers(bubbleId),
    {
      enabled: !!bubbleId,
      staleTime: 1000 * 60 * 5,
      onError: (err) => {
        replaceConsoleError('Error fetching bubble organizers', err);
      }
    }
  );

  /* ───────────────────────── Handlers ───────────────────────── */
  
  const bubble = bubbleData?.bubble;
  const members = membersData?.members || [];
  const organizers = organizersData?.organizers || [];
  const canManage = bubble?.is_host || bubble?.is_admin || false;
  const memberCount = bubble?.member_count || members.length || 0;

  const handleOpenSettings = useCallback(() => {
    setSettingsPanelOpen(true);
  }, []);

  const handleOpenOrganizers = useCallback(() => {
    setOrganizerDrawerOpen(true);
  }, []);

  const handleOpenParticipants = useCallback(() => {
    setParticipantsPanelOpen(true);
  }, []);

  const handleRefreshAll = useCallback(() => {
    refetchBubble();
    refetchOrganizers();
  }, [refetchBubble, refetchOrganizers]);

  /* ───────────────────────── Render Loading/Error States ───────────────────────── */
  
  if (bubbleLoading) {
    return (
      <SidebarContainer minimized={minimized}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress size={40} thickness={4} />
        </Box>
      </SidebarContainer>
    );
  }

  if (bubbleError || !bubble) {
    return (
      <SidebarContainer minimized={minimized}>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load bubble information
          </Alert>
          <Button 
            fullWidth 
            variant="contained"
            onClick={() => refetchBubble()}
          >
            Retry
          </Button>
        </Box>
      </SidebarContainer>
    );
  }

  /* ───────────────────────── Render Minimized View ───────────────────────── */
  
  if (minimized) {
    return (
      <SidebarContainer minimized>
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Toggle button */}
          <Tooltip title="Expand sidebar" placement="right" arrow>
            <IconButton 
              size="small" 
              onClick={onToggleMinimized}
              sx={{ 
                mb: 1,
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                }
              }}
            >
              <ChevronRight />
            </IconButton>
          </Tooltip>

          <Divider sx={{ mb: 0.5 }} />

          {/* Back */}
          {onNavigateBack && (
            <Tooltip title="Back to Hub" placement="right" arrow>
              <MinimizedIcon onClick={onNavigateBack}>
                <ArrowBackIcon fontSize="small" />
              </MinimizedIcon>
            </Tooltip>
          )}

          {/* Participants */}
          <Tooltip title={`Participants (${memberCount})`} placement="right" arrow>
            <MinimizedIcon onClick={handleOpenParticipants}>
              <Badge 
                badgeContent={memberCount} 
                color="primary"
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    height: 16,
                    minWidth: 16,
                    padding: '0 4px',
                  }
                }}
              >
                <PeopleIcon fontSize="small" />
              </Badge>
            </MinimizedIcon>
          </Tooltip>

          {/* Organizers */}
          <Tooltip title={`Organizers (${organizers.length})`} placement="right" arrow>
            <MinimizedIcon onClick={handleOpenOrganizers}>
              <Badge 
                badgeContent={organizers.length} 
                color="secondary"
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    height: 16,
                    minWidth: 16,
                    padding: '0 4px',
                  }
                }}
              >
                <FolderIcon fontSize="small" />
              </Badge>
            </MinimizedIcon>
          </Tooltip>

          {/* Settings */}
          {canManage && (
            <Tooltip title="Settings" placement="right" arrow>
              <MinimizedIcon onClick={handleOpenSettings}>
                <SettingsIcon fontSize="small" />
              </MinimizedIcon>
            </Tooltip>
          )}
        </Box>
      </SidebarContainer>
    );
  }

  /* ───────────────────────── Render Full View ───────────────────────── */
  
  return (
    <>
      <SidebarContainer>
        {/* Header */}
        <Header>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" fontWeight={700} noWrap sx={{ fontSize: '1.1rem' }}>
                {bubble.name || 'Bubble Info'}
              </Typography>
              <StatusBadge isOnline={isPolling}>
                <PulsingDot isOnline={isPolling} />
                <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.7rem' }}>
                  {isPolling ? 'Live' : 'Offline'}
                </Typography>
              </StatusBadge>
            </Box>
            <Tooltip title="Minimize" arrow>
              <IconButton 
                size="small" 
                onClick={onToggleMinimized}
                sx={{
                  ml: 1,
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  }
                }}
              >
                <ChevronLeft />
              </IconButton>
            </Tooltip>
          </Box>
        </Header>

        {/* Content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto', 
          p: 2.5,
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.1),
            borderRadius: 3,
            '&:hover': {
              backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.2),
            },
          },
        }}>
          {/* Back to Chat Hub Button */}
          {onNavigateBack && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onNavigateBack}
              sx={{ 
                mb: 2.5, 
                borderRadius: 2,
                borderColor: (theme) => alpha(theme.palette.divider, 0.3),
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                }
              }}
            >
              Back to Chat Hub
            </Button>
          )}

          {/* Bubble Info Card */}
          <InfoCard sx={{ mb: 2.5 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack spacing={2.5} alignItems="center">
                {/* Avatar */}
                <Box position="relative">
                  <Avatar
                    src={bubble.avatar_image}
                    sx={{ 
                      width: 72, 
                      height: 72,
                      boxShadow: (theme) => `0 4px 16px ${alpha(theme.palette.common.black, 0.1)}`,
                      border: (theme) => `3px solid ${alpha(theme.palette.background.paper, 1)}`,
                    }}
                  >
                    {bubble.name?.[0]?.toUpperCase() || 'B'}
                  </Avatar>
                  {/* Status indicator on avatar */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 2,
                      right: 2,
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: isPolling ? 'success.main' : 'warning.main',
                      border: (theme) => `2px solid ${theme.palette.background.paper}`,
                      boxShadow: (theme) => `0 0 0 2px ${alpha(
                        isPolling ? theme.palette.success.main : theme.palette.warning.main, 
                        0.2
                      )}`,
                    }}
                  />
                </Box>
                
                {/* Description */}
                {bubble.description && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    textAlign="center"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {bubble.description}
                  </Typography>
                )}

                {/* Badges Row */}
                <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center">
                  {/* Public/Private Badge */}
                  {bubble.is_public ? (
                    <Chip 
                      size="small" 
                      icon={<PublicIcon sx={{ fontSize: 14 }} />} 
                      label="Public" 
                      color="success" 
                      variant="outlined"
                      sx={{ 
                        fontWeight: 600,
                        borderWidth: 1.5,
                      }}
                    />
                  ) : (
                    <Chip 
                      size="small" 
                      icon={<LockIcon sx={{ fontSize: 14 }} />} 
                      label="Private" 
                      variant="outlined"
                      sx={{ 
                        fontWeight: 600,
                        borderWidth: 1.5,
                      }}
                    />
                  )}

                  {/* Role Badges - FIXED: Icons passed as props instead of in styles */}
                  {bubble.is_host && (
                    <Chip 
                      size="small" 
                      label="Host" 
                      icon={<StarIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        color: (theme) => theme.palette.primary.contrastText,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 24,
                        boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.common.black, 0.15)}`,
                        border: 'none',
                        '& .MuiChip-icon': {
                          color: 'inherit',
                        }
                      }}
                    />
                  )}
                  {bubble.is_admin && !bubble.is_host && (
                    <Chip 
                      size="small" 
                      label="Admin" 
                      icon={<ShieldIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        background: (theme) => `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                        color: (theme) => theme.palette.info.contrastText,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 24,
                        boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.common.black, 0.15)}`,
                        border: 'none',
                        '& .MuiChip-icon': {
                          color: 'inherit',
                        }
                      }}
                    />
                  )}
                </Box>
              </Stack>
            </CardContent>
          </InfoCard>

          {/* Quick Actions Section */}
          <Box>
            <SectionLabel>Quick Actions</SectionLabel>
            <Stack spacing={1.5}>
              {/* Participants */}
              <QuickActionCard 
                variant="outlined" 
                onClick={handleOpenParticipants}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      className="action-icon"
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        transition: 'all 0.25s',
                      }}
                    >
                      <PeopleIcon color="primary" sx={{ fontSize: 20 }} />
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={700} gutterBottom>
                        Participants
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {memberCount} {memberCount === 1 ? 'member' : 'members'}
                      </Typography>
                    </Box>
                    <ChevronRight 
                      className="chevron-icon"
                      sx={{ 
                        fontSize: 20, 
                        color: 'action.disabled',
                        transition: 'transform 0.25s',
                      }} 
                    />
                  </Box>
                </CardContent>
              </QuickActionCard>

              {/* Organizers */}
              <QuickActionCard 
                variant="outlined" 
                onClick={handleOpenOrganizers}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      className="action-icon"
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.08),
                        transition: 'all 0.25s',
                      }}
                    >
                      <FolderIcon color="secondary" sx={{ fontSize: 20 }} />
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={700} gutterBottom>
                        Organizers
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {organizersLoading ? 'Loading...' : `${organizers.length} ${organizers.length === 1 ? 'folder' : 'folders'}`}
                      </Typography>
                    </Box>
                    <ChevronRight 
                      className="chevron-icon"
                      sx={{ 
                        fontSize: 20, 
                        color: 'action.disabled',
                        transition: 'transform 0.25s',
                      }} 
                    />
                  </Box>
                </CardContent>
              </QuickActionCard>

              {/* Settings */}
              {canManage && (
                <QuickActionCard 
                  variant="outlined" 
                  onClick={handleOpenSettings}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        className="action-icon"
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08),
                          transition: 'all 0.25s',
                        }}
                      >
                        <SettingsIcon color="info" sx={{ fontSize: 20 }} />
                      </Box>
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body2" fontWeight={700} gutterBottom>
                          Bubble Settings
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Manage bubble configuration
                        </Typography>
                      </Box>
                      <ChevronRight 
                        className="chevron-icon"
                        sx={{ 
                          fontSize: 20, 
                          color: 'action.disabled',
                          transition: 'transform 0.25s',
                        }} 
                      />
                    </Box>
                  </CardContent>
                </QuickActionCard>
              )}
            </Stack>
          </Box>
        </Box>
      </SidebarContainer>

      {/* Comprehensive Settings Panel */}
      <BubbleSettingsPanel
        open={settingsPanelOpen}
        onClose={() => setSettingsPanelOpen(false)}
        bubble={bubble}
        onSave={handleRefreshAll}
      />

      {/* Comprehensive Participants Panel */}
      <BubbleParticipantsPanel
        open={participantsPanelOpen}
        onClose={() => setParticipantsPanelOpen(false)}
        bubble={bubble}
        conversationId={conversationId}
        canManage={canManage}
      />

      {/* Organizers Drawer */}
      <Drawer
        anchor="left"
        open={organizerDrawerOpen}
        onClose={() => setOrganizerDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            maxWidth: '100%',
          }
        }}
      >
        <BubbleOrganizerSidebar
          bubbleId={bubbleId}
          organizers={organizers}
          open={organizerDrawerOpen}
          onClose={() => setOrganizerDrawerOpen(false)}
          onRefresh={refetchOrganizers}
          canManage={canManage}
        />
      </Drawer>
    </>
  );
}